import { Component, Host, h, State, Fragment } from '@stencil/core';
import cls from 'classnames';
import { storageUtils, OKSDK_DEV_SETTINGS } from '../../../utils/storageUtils';

interface DevSettings {
  [k: string]: any;
}

const defaultSettings = {
  lang: 'en',
  searchMode: undefined,
  enableDarkMode: false,
  enableMapDarkMode: false,
  enableMedicalTerm: false,
  screenLayout: 'desktop',
  hideSearchBar: undefined,
  currentPosition: null,
  searchNearMeScreen: false,
  exitPopupExternalLink: false,
  'hideSearchField-check': false,
};

const storeSettings = (settings: DevSettings) => {
  storageUtils.setObject(OKSDK_DEV_SETTINGS, settings);
};

const loadSettings = (): DevSettings => {
  return storageUtils.getObject(OKSDK_DEV_SETTINGS, defaultSettings);
};

const optionSets = [
  {
    key: 'lang',
    label: 'Language',
    type: 'select',
    options: [
      { label: 'Deutsch', value: 'de_DE' },
      { label: 'English', value: 'en' },
      { label: 'Español', value: 'es_ES' },
      { label: 'Español (CO)', value: 'es_CO' },
      { label: 'Français', value: 'fr_FR' },
      { label: 'Français (BE)', value: 'fr_BE' },
      { label: 'Français (CA)', value: 'fr_CA' },
      { label: 'Italiano', value: 'it_IT' },
      { label: 'Nederlands', value: 'nl_NL' },
      { label: 'Polski', value: 'pl_PL' },
      { label: 'Português (PT)', value: 'pt_PT' },
      { label: 'Türkçe', value: 'tr_TR' },
      { label: 'Română', value: 'ro_RO' },
      { label: 'Pусский', value: 'ru_RU' },
      { label: 'العربیة', value: 'ar_SA' },
      { label: 'עברית', value: 'he_IL' },
    ],
  },
  {
    key: 'screenLayout',
    label: 'Screen Layout',
    type: 'select',
    options: [
      { label: 'Desktop', value: 'desktop' },
      { label: 'Mobile', value: 'mobile' },
      { label: 'Tablet', value: 'tablet' },
    ],
  },
  {
    key: 'searchMode',
    label: 'Search Mode',
    type: 'select',
    options: [
      { label: 'Start with HCP', value: 'startWithHCP' },
      { label: 'Start with HCO', value: 'startWithHCO' },
      { label: 'Only HCP', value: 'onlyHCP' },
      { label: 'Only HCO', value: 'onlyHCO' },
    ],
  },
  {
    key: 'apiKey',
    label: 'API Key',
    type: 'text',
  },
  {
    key: 'enableMedicalTerm',
    label: 'Search Medical Terms',
    type: 'checkbox',
  },
  {
    key: 'enableDarkMode',
    label: 'Dark Mode',
    type: 'checkbox',
  },
  {
    key: 'enableMapDarkMode',
    label: 'Night Mode for Map',
    type: 'checkbox',
  },
  {
    key: 'searchNearMeScreen',
    label: 'Enable Search Near Screen example',
    type: 'checkbox',
  },
  {
    key: 'useGetCurrentPosition',
    label: 'Use getCurrentPosition',
    type: 'checkbox',
  },
  {
    key: 'disableAppointment',
    label: 'Disable Appointment',
    type: 'checkbox',
  },
  {
    key: 'disableIndividualsInHcoCard',
    label: 'Disable Individual in HCO',
    type: 'checkbox',
  },
  {
    key: 'distanceDefault',
    label: 'Distance',
    type: 'text',
  },
  {
    key: 'distanceUnit',
    label: 'Distance unit',
    type: 'select',
    options: [
      { label: 'kilometer', value: 'km' },
      { label: 'miles', value: 'mi' },
    ],
  },
  {
    key: 'currentPosition',
    label: 'Current Position',
    type: 'select',
    options: [
      {
        label: 'San Francisco',
        value: JSON.stringify({ lat: 37.774929, lng: -122.419416 }),
      },
      {
        label: 'New York',
        value: JSON.stringify({ lat: 40.6976701, lng: -74.259864 }),
      },
    ],
  },
  {
    key: 'hideSearchBar', // Make sure to have enough settings to getting into result page before hiding the search bar
    label: 'Hide Search Bar',
    type: 'checkbox',
  },
  {
    key: 'searchByLocation',
    label: 'Search By Location',
    type: 'checkbox',
    separator: true,
  },
  {
    key: 'defaultLocation',
    label: 'Default Location',
    type: 'select',
    options: [
      { label: 'Near me', value: 'nearMe' },
      { label: 'Input Location', value: 'coords' },
    ],
    condition: {
      dependOn: 'searchByLocation',
      value: true,
    },
  },
  {
    key: 'defaultLat',
    label: 'Latitude',
    type: 'text',
    condition: {
      dependOn: 'defaultLocation',
      value: 'coords',
    },
  },
  {
    key: 'defaultLng',
    label: 'Longitude',
    type: 'text',
    condition: {
      dependOn: 'defaultLocation',
      value: 'coords',
    },
  },
  {
    key: 'hideBackHomeButton',
    label: 'Hide Back To Home Button',
    type: 'checkbox',
    separator: true,
  },
  {
    key: 'hideDistanceData',
    label: 'Hide Distance Data',
    type: 'checkbox',
  },
  {
    key: 'hideFeedbackInfo',
    label: 'Hide Feedback info',
    type: 'checkbox',
  },
  {
    key: 'hcpRightPanel',
    label: 'HCP right panel',
    type: 'checkbox',
  },
  {
    key: 'hideTotalResult',
    label: 'Hide count total results',
    type: 'checkbox',
  },
  {
    key: 'hideSortPanel',
    label: 'Hide sort panel',
    type: 'checkbox',
  },
  {
    key: 'hideBackToSearch',
    label: 'Hide back to search',
    type: 'checkbox',
  },
  {
    key: 'hideShareButton',
    label: 'Hide share button',
    type: 'checkbox',
  },
  {
    key: 'sortWorkplacesByItsInputOrder',
    label: 'Sort workplaces input orders',
    type: 'checkbox',
  },
  {
    key: 'showSuggestModification',
    label: 'Show Suggest Modification',
    type: 'checkbox',
  },
  {
    key: 'disableMapListMode',
    label: 'Disable Map list mode',
    type: 'checkbox',
  },
  {
    key: 'disableCollectGeo',
    label: 'Disable collect Geo',
    type: 'checkbox',
  },
  {
    key: 'countriesNarrowList',
    label: 'Narrow country list',
    type: 'text',
  },
  {
    key: 'countryFilterSelected',
    label: 'Country search selected init',
    type: 'text',
  },
  {
    key: 'hidePhoneUtilIcon',
    label: 'Hide phone icon',
    type: 'checkbox',
  },
  {
    key: 'hideHCOTypeLabel',
    label: 'Hide HCO type label',
    type: 'checkbox',
  },
  {
    key: 'hideSpecialtySearch',
    label: 'Hide Specialty Search',
    type: 'checkbox',
  },
  {
    key: 'hideSearchField-check',
    label: 'Hide Search Field Setting',
    type: 'checkbox'
  },
  {
    key: 'hideNameSearchField',
    label: 'Hide Name Search',
    type: 'select',
    options: [
      { label: 'none', value: 'none' },
      { label: 'onlyHCP', value: 'onlyHCP' },
      { label: 'onlyHCO', value: 'onlyHCO' },
      { label: 'all', value: 'all' },
    ],
    condition: {
      dependOn: 'hideSearchField-check',
      value: true,
    },
  },
  {
    key: 'hideSpecialtySearchField',
    label: 'Hide Specialty Search',
    type: 'select',
    options: [
      { label: 'none', value: 'none' },
      { label: 'onlyHCP', value: 'onlyHCP' },
      { label: 'onlyHCO', value: 'onlyHCO' },
      { label: 'all', value: 'all' },
    ],
    condition: {
      dependOn: 'hideSearchField-check',
      value: true,
    },
  },
  {
    key: 'hideAddressSearchField',
    label: 'Hide Address Search',
    type: 'select',
    options: [
      { label: 'none', value: 'none' },
      { label: 'onlyHCP', value: 'onlyHCP' },
      { label: 'onlyHCO', value: 'onlyHCO' },
      { label: 'all', value: 'all' },
    ],
    condition: {
      dependOn: 'hideSearchField-check',
      value: true,
    },
  },
  {
    key: 'hideCountrySearchField',
    label: 'Hide Country Search',
    type: 'select',
    options: [
      { label: 'none', value: 'none' },
      { label: 'onlyHCP', value: 'onlyHCP' },
      { label: 'onlyHCO', value: 'onlyHCO' },
      { label: 'all', value: 'all' },
    ],
    condition: {
      dependOn: 'hideSearchField-check',
      value: true,
    },
  },
  {
    key: 'hideLocationSearch',
    label: 'Hide Location Search',
    type: 'checkbox',
  },
  {
    key: 'hideHomeIntro',
    label: 'Hide Home Introduction',
    type: 'checkbox',
  },
  {
    key: 'changeLocaleBySearchCountry',
    label: 'Locale popup when country changed',
    type: 'checkbox',
  },
  {
    key: 'enablePhoneNumberFormat',
    label: 'Localize phone number format',
    type: 'checkbox',
  },
  {
    key: 'exitPopupExternalLink-check',
    label: 'Exit popup on link cliked',
    type: 'checkbox',
  },
  {
    key: 'exitPopup-title',
    label: 'Exit popup - title',
    type: 'text',
    condition: {
      dependOn: 'exitPopupExternalLink-check',
      value: true,
    },
  },
  {
    key: 'exitPopup-description',
    label: 'Exit popup - description',
    type: 'text',
    condition: {
      dependOn: 'exitPopupExternalLink-check',
      value: true,
    },
  },
  {
    key: 'exitPopup-accept-btn',
    label: 'Exit popup - accept button',
    type: 'text',
    condition: {
      dependOn: 'exitPopupExternalLink-check',
      value: true,
    },
  },
  {
    key: 'exitPopup-cancel-btn',
    label: 'Exit popup - cancel button',
    type: 'text',
    condition: {
      dependOn: 'exitPopupExternalLink-check',
      value: true,
    },
  },
  {
    key: 'exitPopup-accept-style',
    label: 'Accept btn style',
    type: 'select',
    options: [
      { label: 'primary', value: 'primary' },
      { label: 'primary-reverse', value: 'primary-reverse' },
      { label: 'secondary', value: 'secondary' },
      { label: 'secondary-reverse', value: 'secondary-reverse' },
      { label: 'no-styled', value: 'no-styled' },
    ],
    condition: {
      dependOn: 'exitPopupExternalLink-check',
      value: true,
    },
  },
  {
    key: 'exitPopup-cancel-style',
    label: 'Cancel btn style',
    type: 'select',
    options: [
      { label: 'primary-reverse', value: 'primary-reverse' },
      { label: 'primary', value: 'primary' },
      { label: 'secondary', value: 'secondary' },
      { label: 'secondary-reverse', value: 'secondary-reverse' },
      { label: 'no-styled', value: 'no-styled' },
    ],
    condition: {
      dependOn: 'exitPopupExternalLink-check',
      value: true,
    },
  },
];

type OptionsSetsType = typeof optionSets;

@Component({
  tag: 'hcl-sdk-dev-settings',
  styleUrl: 'hcl-sdk-dev-settings.scss',
})
export class HclSDKViewport {
  @State() settings: DevSettings = loadSettings() || defaultSettings;
  @State() isCollapsed = false;

  componentWillLoad() {
    this.applySettings();
  }

  applySettings() {
    const hclSdkEl = document.querySelector('hcl-sdk');
    const wrapper = document.querySelector('.hcl-sdk-wrapper');
    wrapper.classList.remove(
      ...optionSets
        .find(o => o.key === 'screenLayout')
        .options.map(o => o.value),
    );
    wrapper.classList.add(this.settings.screenLayout);

    if (
      this.settings.lang &&
      document.documentElement.lang !== this.settings.lang
    ) {
      document.documentElement.lang = this.settings.lang;
    }
    if (hclSdkEl) {
      hclSdkEl.updateConfig({
        enableMedicalTerm: this.settings.enableMedicalTerm,
      });
    }
  }

  changeExitPopup(k, v) {
    if (k === 'exitPopupExternalLink-check' && v === true) {
      this.settings.exitPopupExternalLink = {
        title: this.settings['exitPopup-title'],
        description: this.settings['exitPopup-description'],
        acceptButton: this.settings['exitPopup-accept-btn'],
        cancelButton: this.settings['exitPopup-cancel-btn'],
        acceptButtonStyled:
          this.settings['exitPopup-accept-style'] || 'primary',
        cancelButtonStyled:
          this.settings['exitPopup-cancel-style'] || 'primary',
      };
    } else if (k === 'exitPopupExternalLink-check' && !v) {
      this.settings.exitPopupExternalLink = false;
    }

    if (k === 'exitPopup-title') {
      this.settings.exitPopupExternalLink.title = v;
    }
    if (k === 'exitPopup-description') {
      this.settings.exitPopupExternalLink.description = v;
    }
    if (k === 'exitPopup-accept-btn') {
      this.settings.exitPopupExternalLink.acceptButton = v;
    }
    if (k === 'exitPopup-cancel-btn') {
      this.settings.exitPopupExternalLink.cancelButton = v;
    }
    if (k === 'exitPopup-accept-style') {
      this.settings.exitPopupExternalLink.acceptButtonStyled = v;
    }
    if (k === 'exitPopup-cancel-style') {
      this.settings.exitPopupExternalLink.cancelButtonStyled = v;
    }
  }

  changHideSearchField(k, v) {
    if (k === 'hideSearchField-check' && v === true) {
      this.settings.hideSearchField = {
        name: this.settings.hideNameSearchField,
        specialty: this.settings.hideSpecialtySearchField,
        address: this.settings.hideAddressSearchField,
        country: this.settings.hideCountrySearchField,
      }
    } else if (k === 'hideSearchField-check' && !v) {
      this.settings.hideSearchField = {};
    }
    if (k === 'hideNameSearchField') {
      this.settings.hideSearchField.name = v;
    }
    if (k === 'hideSpecialtySearchField') {
      this.settings.hideSearchField.specialty = v;
    }
    if (k === 'hideAddressSearchField') {
      this.settings.hideSearchField.address = v;
    }
    if (k === 'hideCountrySearchField') {
      this.settings.hideSearchField.country = v;
    }
  }

  changeSetting(k, v) {
    this.settings = {
      ...this.settings,
      [k]: v,
    };
    if (k === 'searchNearMeScreen' && v === true) {
      this.settings.currentPosition = JSON.stringify({
        lat: 37.774929,
        lng: -122.419416,
      });
    } else if (k === 'searchNearMeScreen' && v === false) {
      this.settings.currentPosition = undefined;
    }

    this.changHideSearchField(k,v)

    this.changeExitPopup(k, v);

    storeSettings(this.settings);

    if (k === 'currentPosition' && this.settings.searchNearMeScreen) {
      const hclSdkEl = document.querySelector('hcl-sdk');

      if (hclSdkEl) {
        hclSdkEl.currentPosition = JSON.parse(this.settings.currentPosition);
      }
    } else {
      this.applySettings();
    }
  }

  handleCollapseBtnClick = () => {
    this.isCollapsed = !this.isCollapsed;
  };

  isMatchedDependCondition(setting, allOptions: OptionsSetsType) {
    if (!setting.condition || !setting.condition.dependOn) {
      return true;
    }

    const dependSetting = allOptions.find(
      o => o.key === setting.condition.dependOn,
    );

    if (!this.isMatchedDependCondition(dependSetting, allOptions)) {
      return false;
    }

    if (this.settings[setting.condition.dependOn] === setting.condition.value) {
      return true;
    }

    return false;
  }

  renderSetting(setting, allOptions: OptionsSetsType) {
    const isHide = !this.isMatchedDependCondition(setting, allOptions);
    if (isHide) {
      return null;
    }
    return (
      <Fragment>
        {setting.separator && <div class="separator"></div>}
        <div class="setting">
          <label>{setting.label}</label>
          {setting.type === 'select' && (
            <select
              onChange={e =>
                this.changeSetting(setting.key, (e.target as any).value)
              }
            >
              {setting.options.map(option => (
                <option
                  value={option.value}
                  selected={this.settings[setting.key] === option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          )}
          {setting.type === 'text' && (
            <input
              value={this.settings[setting.key]}
              onChange={e =>
                this.changeSetting(setting.key, (e.target as any).value)
              }
            />
          )}
          {setting.type === 'checkbox' && (
            <input
              type="checkbox"
              checked={this.settings[setting.key]}
              onChange={e =>
                this.changeSetting(setting.key, (e.target as any).checked)
              }
            />
          )}
        </div>
      </Fragment>
    );
  }

  render() {
    const className = cls('dev-settings', {
      collapsed: this.isCollapsed,
    });
    return (
      <Host>
        <slot></slot>
        <div
          class={cls('dev-settings__wrapper', { collapsed: this.isCollapsed })}
        >
          <div
            class="dev-settings-collapse-btn"
            onClick={this.handleCollapseBtnClick}
          >
            {this.isCollapsed ? '<' : '>'}
          </div>
          <div class={className}>
            {optionSets.map(optionSet =>
              this.renderSetting(optionSet, optionSets),
            )}
          </div>
        </div>
      </Host>
    );
  }
}
